import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import type { AuthState, LoginCredentials, RegisterData, UserTeam } from '../utils/types';
import { authService } from '../services/auth.service';

interface AuthStore extends AuthState {
  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  initializeAuth: () => Promise<void>;
  fetchUserTeams: () => Promise<UserTeam[]>;
  switchTeam: (teamId: string) => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      team: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null });
          const response = await authService.login(credentials);
          const { user, team, accessToken, refreshToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            team,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          toast.success(`Welcome back, ${user.firstName}!`);
        } catch (error: any) {
          const message = error.response?.data?.message || 'Login failed';
          set({ isLoading: false, error: message });
          toast.error(message);
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true, error: null });

          // ⬇️ remove confirmPassword before sending to API
          const { confirmPassword, ...payload } = data;

          const response = await authService.register(payload);
          const { user, team, accessToken, refreshToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            team,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          toast.success('Account created successfully!');
        } catch (error: any) {
          const message = error.response?.data?.message || 'Registration failed';
          set({ isLoading: false, error: message });
          toast.error(message);
          throw error;
        }
      },


      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            user: null,
            team: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          toast.success('You have been logged out.');
        }
      },

      refreshToken: async () => {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) throw new Error('No refresh token available');

          const response = await authService.refreshToken(refreshToken);
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
        } catch (error) {
          await get().logout();
          toast.error('Session expired, please log in again.');
          throw error;
        }
      },

      forgotPassword: async (email: string) => {
        try {
          set({ isLoading: true, error: null });
          await authService.forgotPassword(email);
          set({ isLoading: false });
          toast.success('Password reset link sent to your email.');
        } catch (error: any) {
          const message = error.response?.data?.message || 'Failed to send reset email';
          set({ isLoading: false, error: message });
          toast.error(message);
          throw error;
        }
      },

      resetPassword: async (token: string, password: string) => {
        try {
          set({ isLoading: true, error: null });
          await authService.resetPassword(token, password);
          set({ isLoading: false });
          toast.success('Password reset successfully! You can log in now.');
        } catch (error: any) {
          const message = error.response?.data?.message || 'Failed to reset password';
          set({ isLoading: false, error: message });
          toast.error(message);
          throw error;
        }
      },

      // Fetch user's teams
      fetchUserTeams: async (): Promise<UserTeam[]> => {
        try {
          const response = await authService.getUserTeams();
          return response.data;
        } catch (error: any) {
          const message = error.response?.data?.message || 'Failed to fetch teams';
          toast.error(message);
          throw error;
        }
      },
      
      // Switch team
      switchTeam: async (teamId: string): Promise<void> => {
        try {
          set({ isLoading: true, error: null });
          const response = await authService.switchTeam(teamId);
          const { user, team, accessToken, refreshToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            team,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          toast.success(`Switched to ${team.name} team`);
        } catch (error: any) {
          const message = error.response?.data?.message || 'Failed to switch team';
          set({ isLoading: false, error: message });
          toast.error(message);
          throw error;
        }
      },


      initializeAuth: async () => {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');

        if (accessToken && refreshToken) {
          try {
            const response = await authService.getCurrentUser();
            const { user, team } = response.data;

            set({ user, team, isAuthenticated: true });
          } catch (error) {
            try {
              await get().refreshToken();
              const response = await authService.getCurrentUser();
              const { user, team } = response.data;

              set({ user, team, isAuthenticated: true });
            } catch {
              await get().logout();
            }
          }
        }
      },

      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        team: state.team,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Auth headers
export function getAuthHeaders(): Record<string, string | undefined> {
  const token = localStorage.getItem('accessToken');
  return {
    Authorization: token ? `Bearer ${token}` : undefined,
  };
}
